import { memo } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { Checkbox, IconButton, InputAdornment, TableCell, TableRow, TextField } from '@mui/material';
import { SelectMenu } from '../../../Atoms/SelectMenu';
import { TextBlock } from '../../../Atoms/Text';
import { LineField } from './currentSupplierLimits';
import { categoryOptions } from './InvoiceView.calc';
import { DraftLine, lineErrors } from './manualSpendDraft.calc';

export interface ManualLineRowProps {
  line: DraftLine;
  number: number;
  categories: string[];
  checked: boolean;
  disabled: boolean;
  onFieldChange: (key: string, field: LineField, text: string) => void;
  onCategoryChange: (key: string, category: string) => void;
  onScopeChange: (key: string, inScope: boolean) => void;
  onRemove: (key: string) => void;
}

const FIELDS: { field: LineField; label: string; placeholder: string; dollars?: boolean }[] = [
  { field: 'description', label: 'Description', placeholder: 'Add a description…' },
  { field: 'quantity', label: 'Quantity', placeholder: '0' },
  { field: 'unit_cost', label: 'Unit cost', placeholder: '0.00', dollars: true },
  { field: 'monthly_cost', label: 'Monthly cost', placeholder: '0.00', dollars: true }
];

const categoryChoices = (categories: string[], current: string) => [
  { value: '', text: 'Select category', disabled: true },
  ...(current ? categoryOptions(categories, current) : categories).map((category) => ({
    value: category,
    text: category
  }))
];

const LineRow = ({ line, number, categories, checked, disabled, ...on }: ManualLineRowProps) => {
  const errors = checked ? lineErrors(line) : {};
  return (
    <TableRow className="align-top">
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          checked={line.in_scope}
          disabled={disabled}
          inputProps={{ 'aria-label': `In scope, line ${number}` }}
          onChange={(event) => on.onScopeChange(line.key, event.target.checked)}
        />
      </TableCell>
      <TableCell className="min-w-40">
        <SelectMenu
          name={`category-line-${number}`}
          ariaLabel={`Category, line ${number}`}
          value={line.category}
          disabled={disabled}
          selectClasses={`${errors.category ? '!border-red' : ''} ${line.category ? '' : '!text-grey-4'}`}
          options={categoryChoices(categories, line.category)}
          handleSelect={(event) => on.onCategoryChange(line.key, event.target.value)}
        />
        {errors.category && (
          <TextBlock
            size="xs"
            color="red">
            {errors.category}
          </TextBlock>
        )}
      </TableCell>
      {FIELDS.map(({ field, label, placeholder, dollars }) => (
        <TableCell key={field}>
          <TextField
            size="small"
            fullWidth
            value={line[field]}
            disabled={disabled}
            placeholder={placeholder}
            error={!!errors[field]}
            helperText={errors[field]}
            inputProps={{
              'aria-label': `${label}, line ${number}`,
              inputMode: field === 'description' ? 'text' : 'decimal'
            }}
            InputProps={dollars ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : undefined}
            onChange={(event) => on.onFieldChange(line.key, field, event.target.value)}
          />
        </TableCell>
      ))}
      <TableCell padding="checkbox">
        <IconButton
          size="small"
          aria-label={`Remove line ${number}`}
          disabled={disabled}
          onClick={() => on.onRemove(line.key)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export const ManualLineRow = memo(LineRow);
